import { Switch, useRouteMatch } from 'react-router-dom';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { Box } from '@givebutter/primitives/box';
import { Button } from '@givebutter/primitives/button';
import { useToast } from '@givebutter/primitives/toast';
import { faSpinner } from '@fortawesome/pro-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { updateCampaign } from 'api/campaigns';
import { CampaignItemBundleFormDrawer } from 'assets/js/pages/campaign-settings/campaign-event/CampaignItemBundleFormDrawer';
import { EventAttendees } from 'assets/js/pages/campaign-settings/campaign-event/EventAttendees';
import { EventDetails } from 'assets/js/pages/campaign-settings/campaign-event/EventDetails';
import { EventPromoCodes } from 'assets/js/pages/campaign-settings/campaign-event/EventPromoCodes';
import { EventTickets } from 'assets/js/pages/campaign-settings/campaign-event/EventTickets';
import { OrganizeItemsFormDrawer } from 'assets/js/pages/campaign-settings/campaign-event/OrganizeItemsFormDrawer';
import { PromoCodeFormDrawer } from 'assets/js/pages/campaign-settings/campaign-event/PromoCodeFormDrawer';
import { TicketFormDrawer } from 'assets/js/pages/campaign-settings/campaign-event/TicketFormDrawer';
import { TableSeatingBuilder } from 'assets/js/pages/campaign-settings/campaign-event/table-seating/TableSeatingBuilder';
import { TableSeatingIndex } from 'assets/js/pages/campaign-settings/campaign-event/table-seating/TableSeatingIndex';
import { ViewSeatingGroupDrawer } from 'assets/js/pages/campaign-settings/campaign-event/table-seating/ViewSeatingGroupDrawer';
import { DrawerRoute } from 'components/drawer-route';
import * as NavMenu from 'components/nav-menu';
import { TicketDrawerRouter } from 'components/pages/tickets/TicketDrawerRouter';
import { Route } from 'components/router/Route';
import * as Tooltip from 'components/tooltip';
import { useCurrentCampaign } from 'customHooks/useCurrentCampaign';
import { useCurrentCampaignEvent } from 'customHooks/useCurrentCampaignEvent';
import { useFlags } from 'launchdarkly-react-client-sdk';
import { campaignQueryKeys } from 'queries/campaign-query-keys';
import { GivebutterAxiosError } from 'types/error';

const CampaignEventRouter = () => {
    const toast = useToast();
    const queryClient = useQueryClient();
    const match = useRouteMatch();
    const { data: campaign } = useCurrentCampaign();
    const { data: event } = useCurrentCampaignEvent();
    const { tableSeating = false } = useFlags();

    const { mutate: convertToEvent, isPending: isLoading } = useMutation({
        mutationFn: () => updateCampaign({ campaignId: campaign.id, data: { type: 'event' } }),
        onSuccess: () => {
            toast.success('Event created.');
            queryClient.invalidateQueries({
                queryKey: campaignQueryKeys.campaign(campaign.id),
            });
        },
        onError: (error: GivebutterAxiosError) => {
            const err = error.response?.data;

            toast.error(typeof err === 'string' ? err : 'Could not create event.');
        },
    });

    const prefix = 'You must save event details before';
    const isOpen = event.id ? false : undefined;

    if (campaign.type !== 'event') {
        return (
            <Box>
                <div className={'grid grid-cols-12 gap-x-8 p-12'}>
                    <div className={'col-span-6 flex flex-col gap-y-8'}>
                        <div className={'flex flex-col gap-y-2'}>
                            <h1 className={'text-3xl font-medium'}>Event Fundraising</h1>

                            <p className={'max-w-sm text-lg font-medium'}>
                                Add event details, sell tickets and collect donations at your next
                                event.
                            </p>
                        </div>

                        <div>
                            <Button
                                variant="solid"
                                onClick={() => convertToEvent()}
                                disabled={isLoading}
                            >
                                {isLoading ? (
                                    <>
                                        <FontAwesomeIcon icon={faSpinner} />
                                        <span>Creating Event</span>
                                    </>
                                ) : (
                                    'Create Your Event'
                                )}
                            </Button>
                        </div>
                    </div>

                    <div className={'col-span-6'}>
                        <img
                            alt="Illustration"
                            src={`/images/illustrations/undraw-festivities.svg`}
                        />
                    </div>
                </div>
            </Box>
        );
    }

    return (
        <div className={'grid grid-cols-10 gap-4'}>
            <NavMenu.Root
                orientation="vertical"
                aria-label="Campaign event navigation"
                className="col-span-full lg:col-span-2"
            >
                <NavMenu.Item to={match.url} exact={true}>
                    Event Details
                </NavMenu.Item>

                <Tooltip.Root open={isOpen}>
                    <Tooltip.Trigger asChild={true}>
                        <div className={'flex flex-col'}>
                            <NavMenu.Item to={`${match.url}/tickets`} disabled={!event.id}>
                                Tickets
                            </NavMenu.Item>
                        </div>
                    </Tooltip.Trigger>
                    <Tooltip.Portal>
                        <Tooltip.Content side={'right'} sideOffset={4} inverse>
                            <Tooltip.Arrow inverse />
                            <p className={'w-48'}>{prefix} adding tickets.</p>
                        </Tooltip.Content>
                    </Tooltip.Portal>
                </Tooltip.Root>

                <Tooltip.Root open={isOpen}>
                    <Tooltip.Trigger asChild={true}>
                        <div className={'flex flex-col'}>
                            <NavMenu.Item to={`${match.url}/attendees`} disabled={!event.id}>
                                Attendees
                            </NavMenu.Item>
                        </div>
                    </Tooltip.Trigger>
                    <Tooltip.Portal>
                        <Tooltip.Content side={'right'} sideOffset={4} inverse>
                            <Tooltip.Arrow inverse />
                            <p className={'w-48'}>{prefix} viewing attendees.</p>
                        </Tooltip.Content>
                    </Tooltip.Portal>
                </Tooltip.Root>

                {tableSeating && (
                    <Tooltip.Root open={isOpen}>
                        <Tooltip.Trigger asChild={true}>
                            <div className={'flex flex-col'}>
                                <NavMenu.Item
                                    to={`${match.url}/table-seating`}
                                    disabled={!event.id}
                                >
                                    Table Seating
                                </NavMenu.Item>
                            </div>
                        </Tooltip.Trigger>
                        <Tooltip.Portal>
                            <Tooltip.Content side={'right'} sideOffset={4} inverse>
                                <Tooltip.Arrow inverse />
                                <p className={'w-48'}>{prefix} setting up table seating.</p>
                            </Tooltip.Content>
                        </Tooltip.Portal>
                    </Tooltip.Root>
                )}

                <Tooltip.Root open={isOpen}>
                    <Tooltip.Trigger asChild={true}>
                        <div className={'flex flex-col'}>
                            <NavMenu.Item to={`${match.url}/promo-codes`} disabled={!event.id}>
                                Promo Codes
                            </NavMenu.Item>
                        </div>
                    </Tooltip.Trigger>
                    <Tooltip.Portal>
                        <Tooltip.Content side={'right'} sideOffset={4} inverse>
                            <Tooltip.Arrow inverse />
                            <p className={'w-48'}>{prefix} adding promo codes.</p>
                        </Tooltip.Content>
                    </Tooltip.Portal>
                </Tooltip.Root>
            </NavMenu.Root>
            <div className={'col-span-full lg:col-span-8'}>
                <Switch>
                    <Route path={`${match.path}/tickets`} component={TicketsRouter} />
                    <Route path={`${match.path}/promo-codes`} component={PromoCodesRouter} />
                    <Route path={`${match.path}/attendees`} component={AttendeesRouter} />
                    {tableSeating && (
                        <Route
                            path={`${match.path}/table-seating`}
                            component={TableSeatingRouter}
                        />
                    )}
                    <Route path={match.path} component={EventDetails} />
                </Switch>
            </div>
        </div>
    );
};

const TicketsRouter = () => {
    const match = useRouteMatch();

    return (
        <>
            <DrawerRoute
                path={[`${match.path}/new`, `${match.path}/:ticket_id/edit`]}
                component={TicketFormDrawer}
            />
            <DrawerRoute path={`${match.path}/organize`} component={OrganizeItemsFormDrawer} />
            <DrawerRoute
                path={[`${match.path}/bundles/new`, `${match.path}/bundles/:bundleId/edit`]}
                component={CampaignItemBundleFormDrawer}
            />
            <Switch>
                <Route path={match.path} component={EventTickets} />
            </Switch>
        </>
    );
};

const AttendeesRouter = () => {
    const match = useRouteMatch();

    return (
        <>
            <DrawerRoute path={`${match.path}/:ticket_id`} component={TicketDrawerRouter} />
            <Switch>
                <Route path={match.path} component={EventAttendees} />
            </Switch>
        </>
    );
};

const TableSeatingRouter = () => {
    const match = useRouteMatch();

    return (
        <>
            <Switch>
                <DrawerRoute
                    path={`${match.path}/:seatingGroupId/ticket/:ticket_id`}
                    component={TicketDrawerRouter}
                />
                <DrawerRoute
                    path={`${match.path}/:seatingGroupId`}
                    component={ViewSeatingGroupDrawer}
                />
            </Switch>
            <Switch>
                <Route path={`${match.path}/builder`} component={TableSeatingBuilder} />
                <Route path={match.path} component={TableSeatingIndex} />
            </Switch>
        </>
    );
};

const PromoCodesRouter = () => {
    const match = useRouteMatch();

    return (
        <>
            <DrawerRoute
                path={[`${match.path}/new`, `${match.path}/:promo_code_id/edit`]}
                component={PromoCodeFormDrawer}
            />
            <Switch>
                <Route path={match.path} component={EventPromoCodes} />
            </Switch>
        </>
    );
};

export { CampaignEventRouter };
